import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NavigationItem } from './types';
import { isRouteActive } from './config';
import { motion } from 'framer-motion';

interface NavBottomBarProps {
  items: NavigationItem[];
  className?: string;
  onNavigate?: (href: string) => void;
}

const NavBottomBar: React.FC<NavBottomBarProps> = ({
  items,
  className,
  onNavigate,
}) => {
  const location = useLocation();
  
  // Add active state to items based on current route
  const navItems = useMemo(() => {
    return items.map(item => ({
      ...item,
      isActive: isRouteActive(item.href, location.pathname),
    }));
  }, [items, location.pathname]);

  // Handle item click
  const handleClick = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    }
  };

  return (
    <div 
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40",
        // Solid background - no glass shader effect  
        "bg-[#1A1A1B] border-t border-white/10",
        // Layout - slightly taller for mobile
        "px-1 py-2 flex items-center justify-around",
        className
      )}
      style={{ 
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0px)'
      }}
    >
      {navItems.map((item) => {
        // Disabled state styling
        const isDisabled = item.disabled;
        
        return (
          <Link
            key={item.id}
            to={item.href}
            className={cn(
              "flex flex-col items-center justify-center py-1.5 px-2.5 rounded-xl relative",
              "transition-colors text-center min-w-[56px]",
              item.isActive 
                ? "text-white" 
                : "text-[#9b9da1] hover:text-[#e5e7eb]",
              isDisabled && "opacity-50 cursor-not-allowed"
            )}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                return;
              }
              handleClick(item.href);
            }}
          >
            {item.isActive && (
              <motion.div
                layoutId="nav-indicator"
                className="absolute inset-0 rounded-xl -z-10"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)'
                }}
                transition={{ type: 'spring', duration: 0.45, stiffness: 300, damping: 30 }}
              />
            )}
            
            <span className="relative inline-flex items-center justify-center">
              <item.icon
                size={18}
                strokeWidth={0}
                fill="currentColor"
                className={cn(
                  "mx-auto drop-shadow-[0_1px_1px_rgba(0,0,0,0.35)]",
                  item.isActive ? "text-white" : "text-current"
                )}
              />
              {item.badge && typeof item.badge === 'number' && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </span>
            
            <span className={cn(
              "text-[8px] mt-0.5",
              item.isActive ? "text-white" : "text-[#a6a9ae]"
            )}>{item.label}</span>
          </Link>
        );
      })}
    </div>
  );
};

export default NavBottomBar;
